import React from 'react'
import { Tooltip } from '@mui/material'
import {
  Add as AddIcon, ArrowBack, ArrowForward, DarkMode as DarkModeIcon, DeleteOutline, EditOutlined,
  LightMode as LightModeIcon, Search as SearchIcon, Star as StarIcon, Business as BusinessIcon, CardGiftcard as GiftIcon, Hub as HubIcon, AutoAwesome as AiIcon,
  ContentCopy as CopyIcon, LocalOfferOutlined as TagIcon, PowerSettingsNew as PowerIcon, EuroSymbol as EuroIcon,
} from '@mui/icons-material'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { PopupPackageV2 } from '../component/package-v2/PopupPackageV2'
import { PopupWorkflowV2 } from '../component/package-v2/PopupWorkflowV2'
import { ConfirmV2 } from '../component/ConfirmV2'
import { PackagePreviewDialog } from '../component/package-v2/PackagePreviewDialog'
import { WorkflowPreviewDialog } from '../component/package-v2/WorkflowPreviewDialog'
import { DuplicateWorkflowDialog } from '../component/package-v2/DuplicateWorkflowDialog'
import { AuditLog } from '../component/package-v2/AuditLog'
import { canDuplicate, DuplicatePackageDialog } from '../component/package-v2/DuplicatePackageDialog'
import { TableRef } from '../component/datatable/types'
import { useDispatch } from '../store'
import { api } from '../function/axios'
import { deletePackage, deleteWorkflow } from '../store/package/actionCreator'
import { clearFieldError, setTable, showSnackbar } from '../store/common/actionCreator'
import { Package, Workflow } from '../store/package/reducer'
import { PACKAGE_TYPES_LABEL, SERVICE_TYPE_LABEL } from '../enum/serviceTypesNames'
import { useAdminTheme } from '../hook/useAdminTheme'
import sprite_ from '../sass/sprite.module.sass'
import { SourceIcon } from '../component/source-icon/SourceIcon'
import l from './SubscribersPageV2.module.css'
import s from './PackagePageV2.module.css'

/*
 * Packages & workflows in the v2 admin design. The classic page
 * (PackagePage.tsx) is untouched; this one reads the same endpoints
 * (/package/admin, /lookup/admin) and edits through the same dialogs
 * (PopupPackage, PopupWorkflow; ConfirmV2 for the questions) with the same rules — the last
 * workflow cannot be deleted, a workflow's delete warns which packages use it,
 * a package needs at least one workflow.
 *
 * Both lists are short, so each is loaded whole and searched, filtered and
 * paged here. They are also put in the redux tables the dialogs read
 * (tables.workflow / tables.package — the lookups to pick from, "already have
 * a custom / demo package"), exactly where the classic page's DataTables left
 * them.
 */

const PER_PAGE = 10
type Tab = 'packages' | 'workflows' | 'changes'
type YesNo = 'all' | 'yes' | 'no'

/* the classic page hides these internal quota sources from the icon row */
const HIDDEN_SOURCES = ['quotaprofiler-periodic', 'quotaprofiler-monitoring', 'quotawebint-lookup', 'quotawebint-periodic']
/* the sprite icon the page showed before the icon collections — what the Text collection keeps showing */
const SOCIAL_API = ['fb_api', 'instagram_api', 'x_api', 'linkedin_api', 'vk_api', 'ok_api', 'tiktok_api', 'blsk_api']
const sourceClass = (src: string) => (sprite_ as Record<string, string>)[
  `social__small_${SOCIAL_API.includes(src) ? src.replace('_api', '') : src}`
]

const payProProduct = (id: number | null) => id && window.open(`https://cc.payproglobal.com/Products/Edit/${id}?isWizard=False`, '_blank', 'noopener,noreferrer')
const payProPrice = (id: number | null) => id && window.open(`https://cc.payproglobal.com/Products/EditPrices/${id}?isWizard=False`, '_blank', 'noopener,noreferrer')

/* the PayPro checks the classic page shows as six icons — as one list */
function payProIssues(p: Package): { text: string; open: () => void }[] {
  const d = p.description || {}
  const out: { text: string; open: () => void }[] = []
  if (d.checkIsMonthlyName === false) out.push({ text: 'Monthly product name does not match', open: () => payProProduct(p.productId) })
  if (d.checkIsAnnualName === false) out.push({ text: 'Annual product name does not match', open: () => payProProduct(p.annualProductId) })
  if (d.checkIsMonthlyActive === false) out.push({ text: 'Monthly product is not active', open: () => payProProduct(p.productId) })
  if (d.checkIsAnnualActive === false) out.push({ text: 'Annual product is not active', open: () => payProProduct(p.annualProductId) })
  if (d.checkIsMonthlyPrice === false) out.push({ text: 'Monthly price does not match', open: () => payProPrice(p.productId) })
  if (d.checkIsAnnualPrice === false) out.push({ text: 'Annual price does not match', open: () => payProPrice(p.annualProductId) })
  return out
}

const typeLabel = (t: string) => (PACKAGE_TYPES_LABEL as Record<string, string>)[t] || t
const workflowTypeLabel = (t: string) => (SERVICE_TYPE_LABEL as Record<string, string>)[t] || t

/*
 * "Name (copy)", or "(copy 2)", "(copy 3)"… when taken — the backend refuses a
 * second workflow with the same title (case-insensitive).
 */
function copyTitle(title: string, taken: string[]): string {
  const used = new Set(taken.map((x) => x.trim().toLowerCase()))
  const base = `${title.trim()} (copy`
  if (!used.has(`${base})`.toLowerCase())) return `${base})`
  for (let n = 2; ; n += 1) if (!used.has(`${base} ${n})`.toLowerCase())) return `${base} ${n})`
}

async function loadAll(url: string): Promise<any> {
  const { data } = await api({ method: 'GET', url, params: { offset: 0, limit: 1000 } })
  return Array.isArray(data) ? { list: data, count: data.length } : { list: data?.list || [], count: data?.count ?? null }
}

/*
  PAYPRO, ALL SIX CHECKS AT ONCE: a row for the monthly product (M) and one for
  the annual (Y); in each, the product's name, whether it is active, and its
  price. Green — matches; red — does not, and a click opens that product (or
  its prices) in PayPro, as the classic page's icons did; grey — nothing to
  check (no such product: free, custom, enterprise, or no annual plan), or no
  result from the check.
*/
type CheckState = 'ok' | 'bad' | 'na'
const CHECKS: { key: 'Name' | 'Active' | 'Price'; icon: React.ReactNode; ok: string; bad: string }[] = [
  { key: 'Name', icon: <TagIcon sx={{ fontSize: 14 }} />, ok: 'name matches', bad: 'name does not match' },
  { key: 'Active', icon: <PowerIcon sx={{ fontSize: 14 }} />, ok: 'product is active', bad: 'product is not active' },
  { key: 'Price', icon: <EuroIcon sx={{ fontSize: 13 }} />, ok: 'price matches', bad: 'price does not match' },
]

function PayProGrid({ p }: { p: Package }) {
  const d = p.description || {}
  const rows: { label: string; period: 'Monthly' | 'Annual'; productId: number | null }[] = [
    { label: 'M', period: 'Monthly', productId: p.productId },
    { label: 'Y', period: 'Annual', productId: p.annualProductId },
  ]
  return (
    <div className={s.pp}>
      {rows.map((r) => (
        <React.Fragment key={r.label}>
          <span className={s.ppLabel}>{r.label}</span>
          {CHECKS.map((c) => {
            const v = d[`checkIs${r.period}${c.key}`]
            const state: CheckState = !r.productId || v === undefined || v === null ? 'na' : v === false ? 'bad' : 'ok'
            const title = !r.productId
              ? `${r.period} · no PayPro product`
              : state === 'na'
                ? `${r.period} · not checked`
                : `${r.period} · ${state === 'ok' ? c.ok : `${c.bad} — click to fix in PayPro`}`
            const open = () => (c.key === 'Price' ? payProPrice(r.productId) : payProProduct(r.productId))
            return (
              <Tooltip key={c.key} title={title}>
                {state === 'bad' ? (
                  <button type="button" className={`${s.ppIcon} ${s.ppBad}`} onClick={open} aria-label={title}>{c.icon}</button>
                ) : (
                  <span className={`${s.ppIcon} ${state === 'ok' ? s.ppOk : s.ppNa}`} aria-label={title}>{c.icon}</span>
                )}
              </Tooltip>
            )
          })}
        </React.Fragment>
      ))}
    </div>
  )
}

function Pager({ page, pages, total, onPage }: { page: number; pages: number; total: number; onPage: (p: number) => void }) {
  if (pages <= 1) return null
  return (
    <div className={s.pager}>
      <span>{page * PER_PAGE + 1}–{Math.min(total, (page + 1) * PER_PAGE)} of {total}</span>
      <div className={s.pagerBtns}>
        <button type="button" className={s.pgBtn} disabled={page === 0} onClick={() => onPage(page - 1)} aria-label="Previous page"><ArrowBack sx={{ fontSize: 16 }} /></button>
        <button type="button" className={s.pgBtn} disabled={page >= pages - 1} onClick={() => onPage(page + 1)} aria-label="Next page"><ArrowForward sx={{ fontSize: 16 }} /></button>
      </div>
    </div>
  )
}

export function PackagePageV2() {
  const dispatch = useDispatch()
  const { theme, toggleTheme } = useAdminTheme()

  const [tab, setTab] = React.useState<Tab>('packages')
  const [packages, setPackages] = React.useState<Package[] | null>(null)
  const [workflows, setWorkflows] = React.useState<Workflow[] | null>(null)
  const [query, setQuery] = React.useState('')
  const [page, setPage] = React.useState(0)
  // package filters (the classic page's Active / Includes AI / Type)
  const [active, setActive] = React.useState<YesNo>('all')
  const [ai, setAi] = React.useState<YesNo>('all')
  const [pkgType, setPkgType] = React.useState('all')
  const [wfType, setWfType] = React.useState('all')

  const [packageCreateOpen, setPackageCreateOpen] = React.useState(false)
  const [packageEdit, setPackageEdit] = React.useState<Package | null>(null)
  // a click on a package shows it as the user sees it; Edit opens the editor
  const [packagePreview, setPackagePreview] = React.useState<Package | null>(null)
  const [packageDuplicate, setPackageDuplicate] = React.useState<Package | null>(null)
  const [packageDelete, setPackageDelete] = React.useState<number | null>(null)
  const [workflowCreateOpen, setWorkflowCreateOpen] = React.useState(false)
  const [workflowEdit, setWorkflowEdit] = React.useState<Workflow | null>(null)
  // a click on a workflow shows it as the user's Search page does; Edit opens the editor
  const [workflowPreview, setWorkflowPreview] = React.useState<Workflow | null>(null)
  const [workflowCopy, setWorkflowCopy] = React.useState<Workflow | null>(null)
  const [workflowDelete, setWorkflowDelete] = React.useState<number | null>(null)

  const loadPackages = React.useCallback(async () => {
    try {
      const data = await loadAll('/package/admin')
      setPackages(data.list)
      dispatch(setTable(data, 'package'))
    } catch (e) {
      setPackages([])
    }
  }, [dispatch])
  const loadWorkflows = React.useCallback(async () => {
    try {
      const data = await loadAll('/lookup/admin')
      setWorkflows(data.list)
      dispatch(setTable(data, 'workflow'))
    } catch (e) {
      setWorkflows([])
    }
  }, [dispatch])

  React.useEffect(() => { void loadPackages(); void loadWorkflows() }, [loadPackages, loadWorkflows])

  // the dialogs refresh "their table" when they save — here, the list behind it
  const packageRef = React.useRef<TableRef>({ refresh: () => { void loadPackages() }, getSelectedIds: () => [] })
  const workflowRef = React.useRef<TableRef>({ refresh: () => { void loadWorkflows() }, getSelectedIds: () => [] })
  packageRef.current = { refresh: () => { void loadPackages() }, getSelectedIds: () => [] }
  workflowRef.current = { refresh: () => { void loadWorkflows() }, getSelectedIds: () => [] }

  React.useEffect(() => { setPage(0) }, [tab, query, active, ai, pkgType, wfType])

  const q = query.trim().toLowerCase()
  const pkgList = (packages || []).filter((p) => {
    if (active !== 'all' && !!p.isActive !== (active === 'yes')) return false
    if (ai !== 'all' && !!p.isAiAssistant !== (ai === 'yes')) return false
    if (pkgType !== 'all' && p.type !== pkgType) return false
    return !q || [p.title, p.subTitle?.context].some((v) => String(v || '').toLowerCase().includes(q))
  })
  const wfList = (workflows || []).filter((w) => {
    if (wfType !== 'all' && w.type !== wfType) return false
    return !q || [w.title, w.subTitle].some((v) => String(v || '').toLowerCase().includes(q))
  })

  const list: any[] = tab === 'packages' ? pkgList : wfList
  const pages = Math.max(1, Math.ceil(list.length / PER_PAGE))
  const rows = list.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

  const allPkgs = packages || []
  const kpi = {
    total: allPkgs.length,
    active: allPkgs.filter((p) => p.isActive).length,
    ai: allPkgs.filter((p) => p.isAiAssistant).length,
    issues: allPkgs.filter((p) => payProIssues(p).length > 0).length,
    workflows: (workflows || []).length,
  }
  const pkgTypes = Array.from(new Set(allPkgs.map((p) => p.type))).sort()
  const wfTypes = Array.from(new Set((workflows || []).map((w) => w.type))).sort()

  const affectingPackages = workflowDelete == null ? [] : allPkgs.filter((p) => p.lookups?.some((x) => x.id === workflowDelete))

  const createPackage = () => {
    if (!(workflows || []).length) dispatch(showSnackbar('error', 'Must be at least 1 search workflow'))
    else setPackageCreateOpen(true)
  }
  const confirmWorkflowDelete = () => {
    if ((workflows || []).length > 1) dispatch(deleteWorkflow(workflowDelete as number, () => { void loadWorkflows() }))
    else dispatch(showSnackbar('error', 'You cannot delete the last workflow'))
  }

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}>
        <small>Catalog</small>
        <b>Packages</b>
      </div>
      <div className={l.spacer} />
      <label className={l.search}>
        <SearchIcon sx={{ fontSize: 17 }} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={tab === 'packages' ? 'Search packages' : 'Search workflows'} />
      </label>
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  const yesNo = (label: string, value: YesNo, set: (v: YesNo) => void) => (
    <label className={s.filter}>
      {label}
      <select className={s.select} value={value} onChange={(e) => set(e.target.value as YesNo)}>
        <option value="all">All</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </label>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        <div className={`${l.kpis} ${s.kpis}`}>
          <div className={l.kpi}><small>Packages</small><b>{packages ? kpi.total : '—'}</b><span>{kpi.active} active</span></div>
          <div className={l.kpi}><small>Active</small><b>{packages ? kpi.active : '—'}</b><span>{kpi.total - kpi.active} inactive</span></div>
          <div className={l.kpi}><small>With AI assistant</small><b>{packages ? kpi.ai : '—'}</b><span>of {kpi.total}</span></div>
          <div className={l.kpi}>
            <small>PayPro issues</small>
            <b style={{ color: kpi.issues ? 'var(--danger)' : undefined }}>{packages ? kpi.issues : '—'}</b>
            <span>{kpi.issues ? 'packages out of sync' : 'all in sync'}</span>
          </div>
          <div className={l.kpi}><small>Search workflows</small><b>{workflows ? kpi.workflows : '—'}</b><span>in {wfTypes.length} types</span></div>
        </div>

        <div className={l.sec}>
          <div className={s.seg}>
            <button type="button" className={`${s.segBtn} ${tab === 'packages' ? s.segOn : ''}`} onClick={() => setTab('packages')}>
              Packages <span>{allPkgs.length}</span>
            </button>
            <button type="button" className={`${s.segBtn} ${tab === 'workflows' ? s.segOn : ''}`} onClick={() => setTab('workflows')}>
              Search workflows <span>{(workflows || []).length}</span>
            </button>
            <button type="button" className={`${s.segBtn} ${tab === 'changes' ? s.segOn : ''}`} onClick={() => setTab('changes')}>
              Changes
            </button>
          </div>
          <div className={l.spacer} />
          {tab === 'packages' ? (
            <>
              {yesNo('Active', active, setActive)}
              {yesNo('AI', ai, setAi)}
              <label className={s.filter}>
                Type
                <select className={s.select} value={pkgType} onChange={(e) => setPkgType(e.target.value)}>
                  <option value="all">All</option>
                  {pkgTypes.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
                </select>
              </label>
              <button type="button" className={s.primary} onClick={createPackage}><AddIcon sx={{ fontSize: 17 }} />Create package</button>
            </>
          ) : tab === 'changes' ? null : (
            <>
              <label className={s.filter}>
                Type
                <select className={s.select} value={wfType} onChange={(e) => setWfType(e.target.value)}>
                  <option value="all">All</option>
                  {wfTypes.map((t) => <option key={t} value={t}>{workflowTypeLabel(t)}</option>)}
                </select>
              </label>
              <button type="button" className={s.primary} onClick={() => setWorkflowCreateOpen(true)}><AddIcon sx={{ fontSize: 17 }} />Create workflow</button>
            </>
          )}
        </div>

        {tab === 'changes' ? (
          // what admins changed in packages and workflows (admin_audit)
          <div className={l.card}><AuditLog /></div>
        ) : (
        <div className={l.card}>
          <div className={l.scroll}>
            {(tab === 'packages' ? packages : workflows) === null ? (
              <div className={l.empty}>Loading…</div>
            ) : !rows.length ? (
              <div className={l.empty}>{q ? `Nothing matches "${query.trim()}"` : 'Nothing here yet'}</div>
            ) : tab === 'packages' ? (
              <table className={l.table}>
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Type</th>
                    <th>Price</th>
                    <th>Features</th>
                    <th>Status</th>
                    <th>PayPro</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {(rows as Package[]).map((p) => {
                    /*
                      FEATURES: the package switches at a glance — on in colour, off greyed.
                      Enterprise as the classic page reads it (a personal package with
                      no PayPro product); Welcome is the free package new sign-ups get.
                    */
                    const features: { key: string; on: boolean; label: string; icon: React.ReactNode; color: string }[] = [
                      { key: 'best', on: !!p.isBestProposition, label: 'Best offer', icon: <StarIcon sx={{ fontSize: 16 }} />, color: 'var(--warn)' },
                      { key: 'ent', on: p.type === 'personal' && p.productId === null, label: 'Enterprise', icon: <BusinessIcon sx={{ fontSize: 16 }} />, color: 'var(--primary)' },
                      { key: 'welcome', on: !!p.isFree, label: 'Welcome (free package)', icon: <GiftIcon sx={{ fontSize: 16 }} />, color: 'var(--ok)' },
                      { key: 'pro', on: !!p.isIrbisPro, label: 'Irbis Pro', icon: <HubIcon sx={{ fontSize: 16 }} />, color: '#EC4899' },
                      { key: 'ai', on: !!p.isAiAssistant, label: 'AI assistant', icon: <AiIcon sx={{ fontSize: 16 }} />, color: '#7C5CFF' },
                    ]
                    return (
                      <tr key={p.id} onClick={() => setPackagePreview(p)} title="Show as the user sees it">
                        <td>
                          <div className={s.name}>
                            <b>{p.title}</b>
                          </div>
                          {p.subTitle?.context && (
                            p.subTitle.type === 'text'
                              ? <span className={l.sub}>{p.subTitle.context}</span>
                              : <span className={s.subChip} data-custom={p.type === 'custom' ? 'y' : undefined}>{p.subTitle.context}</span>
                          )}
                        </td>
                        <td><span className={s.type}>{typeLabel(p.type)}</span></td>
                        <td className={l.nowrap}>
                          {p.type === 'custom' ? <b>Custom</b> : <b>€ {p.price ?? '—'}</b>}
                          {p.annualPrice != null && p.type !== 'custom' && <span className={l.sub}>€ {p.annualPrice} / year</span>}
                        </td>
                        <td>
                          <div className={s.features}>
                            {features.map((f) => (
                              <Tooltip key={f.key} title={`${f.label}: ${f.on ? 'yes' : 'no'}`}>
                                <span
                                  className={`${s.featureIcon} ${f.on ? '' : s.featureOff}`}
                                  style={f.on ? { color: f.color, background: `color-mix(in srgb, ${f.color} 13%, transparent)` } : undefined}
                                  aria-label={`${f.label}: ${f.on ? 'yes' : 'no'}`}
                                >
                                  {f.icon}
                                </span>
                              </Tooltip>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className={l.status}>
                            <span className={l.dot} style={{ background: p.isActive ? 'var(--ok)' : 'var(--text3)' }} />
                            {p.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <PayProGrid p={p} />
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className={s.actions}>
                            <Tooltip title="Edit"><button type="button" className={s.act} onClick={() => setPackageEdit(p)} aria-label="Edit"><EditOutlined sx={{ fontSize: 17 }} /></button></Tooltip>
                            <Tooltip title={canDuplicate(p) ? 'Duplicate' : 'Only one custom / demo package is allowed'}>
                              <span>
                                <button type="button" className={s.act} disabled={!canDuplicate(p)} onClick={() => setPackageDuplicate(p)} aria-label="Duplicate">
                                  <CopyIcon sx={{ fontSize: 16 }} />
                                </button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete"><button type="button" className={`${s.act} ${s.actDel}`} onClick={() => setPackageDelete(p.id)} aria-label="Delete"><DeleteOutline sx={{ fontSize: 17 }} /></button></Tooltip>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <table className={l.table}>
                <thead>
                  <tr>
                    <th>Workflow</th>
                    <th>Type</th>
                    <th>Price</th>
                    <th>Sources</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {(rows as Workflow[]).map((w) => (
                    <tr key={w.id} onClick={() => setWorkflowPreview(w)} title="Show as the user sees it">
                      <td>
                        <b>{w.title}</b>
                        {w.subTitle && <span className={l.sub}>{w.subTitle}</span>}
                      </td>
                      <td><span className={s.type}>{workflowTypeLabel(w.type)}</span></td>
                      <td className={l.nowrap}>{w.type === 'webint' ? <span className={l.muted}>—</span> : <b>€ {w.price}</b>}</td>
                      <td>
                        <div className={s.sources}>
                          {(w.sources || []).filter((src: any) => !HIDDEN_SOURCES.includes(src)).map((src: any) => (
                            <Tooltip key={src} title={String(src)}>
                              <SourceIcon source={String(src)} className={s.source} fallback={<span className={`${s.source} ${sourceClass(String(src)) || ''}`} />} />
                            </Tooltip>
                          ))}
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className={s.actions}>
                          <Tooltip title="Edit"><button type="button" className={s.act} onClick={() => setWorkflowEdit(w)} aria-label="Edit"><EditOutlined sx={{ fontSize: 17 }} /></button></Tooltip>
                          <Tooltip title="Duplicate"><button type="button" className={s.act} onClick={() => setWorkflowCopy(w)} aria-label="Duplicate"><CopyIcon sx={{ fontSize: 16 }} /></button></Tooltip>
                          <Tooltip title="Delete"><button type="button" className={`${s.act} ${s.actDel}`} onClick={() => setWorkflowDelete(w.id)} aria-label="Delete"><DeleteOutline sx={{ fontSize: 17 }} /></button></Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <Pager page={page} pages={pages} total={list.length} onPage={setPage} />
        </div>
        )}
      </div>

      {packageCreateOpen && (
        <PopupPackageV2 mode="create" tableRef={packageRef} theme={theme} onClose={() => { setPackageCreateOpen(false); dispatch(clearFieldError) }} />
      )}
      {packagePreview && (
        <PackagePreviewDialog
          pkg={packagePreview}
          theme={theme}
          onClose={() => setPackagePreview(null)}
          onEdit={() => { setPackageEdit(packagePreview); setPackagePreview(null) }}
          onDuplicate={canDuplicate(packagePreview) ? () => { setPackageDuplicate(packagePreview); setPackagePreview(null) } : undefined}
        />
      )}
      {packageDuplicate && (
        <DuplicatePackageDialog
          pkg={packageDuplicate}
          theme={theme}
          onClose={() => setPackageDuplicate(null)}
          onCreated={() => { void loadPackages() }}
        />
      )}
      {packageEdit && (
        <PopupPackageV2 mode="edit" tableRef={packageRef} theme={theme} data={packageEdit} onClose={() => { setPackageEdit(null); dispatch(clearFieldError) }} />
      )}
      {workflowCreateOpen && (
        <PopupWorkflowV2 mode="create" tableRef={workflowRef} theme={theme} onClose={() => { setWorkflowCreateOpen(false); dispatch(clearFieldError) }} />
      )}
      {workflowPreview && (
        <WorkflowPreviewDialog
          workflow={workflowPreview}
          workflows={workflows || []}
          packages={allPkgs}
          theme={theme}
          onClose={() => setWorkflowPreview(null)}
          onEdit={(w) => { setWorkflowEdit(w); setWorkflowPreview(null) }}
          onDuplicate={(w) => { setWorkflowCopy(w); setWorkflowPreview(null) }}
        />
      )}
      {workflowCopy && (
        // only the title and price to set; the rest is the original's
        <DuplicateWorkflowDialog
          workflow={workflowCopy}
          title={copyTitle(workflowCopy.title, (workflows || []).map((w) => w.title))}
          takenTitles={(workflows || []).map((w) => w.title)}
          theme={theme}
          onClose={() => setWorkflowCopy(null)}
          onCreated={() => { void loadWorkflows() }}
        />
      )}
      {workflowEdit && (
        <PopupWorkflowV2 mode="edit" tableRef={workflowRef} theme={theme} data={workflowEdit} onClose={() => { setWorkflowEdit(null); dispatch(clearFieldError) }} />
      )}
      {workflowDelete != null && (
        <ConfirmV2 header="Delete this workflow?" onYes={confirmWorkflowDelete} onClose={() => setWorkflowDelete(null)}>
          {/* the classic page's warning */}
          {affectingPackages.length > 0 && (
            <>
              This workflow is using for packages:
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {affectingPackages.map((el) => <li key={el.id}>{el.title}</li>)}
              </ul>
            </>
          )}
        </ConfirmV2>
      )}
      {packageDelete != null && (
        <ConfirmV2
          header="Delete this package?"
          onYes={() => dispatch(deletePackage(packageDelete, () => { void loadPackages() }))}
          onClose={() => setPackageDelete(null)}
        />
      )}
    </PrivatePage>
  )
}
